import type {
  ChangePasswordPayload,
  ConnectRequest,
  ConnectResponse,
  LoginRequest,
  RegisterRequest,
  RegisterResponse,
  TokenResponse,
} from "./model"
import { updateTokens } from "./slice"
import baseApi from "@/base/baseApi"

const api = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<TokenResponse, LoginRequest>({
      query: (body) => ({
        url: "security/login",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(updateTokens(data))
        } catch {
          // noop
        }
      },
    }),
    connectOauth: build.mutation<ConnectResponse, ConnectRequest>({
      query: ({ provider, locale }) => ({
        url: `oauth/connect/${provider}`,
        method: "POST",
        params: { locale },
      }),
      extraOptions: { withoutAuth: true },
    }),
    register: build.mutation<RegisterResponse, RegisterRequest>({
      query: (body) => ({
        url: "register",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          // Passwordless signups get a sign-in link email, not tokens
          if ("token" in data) {
            dispatch(updateTokens(data))
          }
        } catch {
          // noop
        }
      },
    }),
    applyLoginLink: build.mutation<TokenResponse, Record<string, string>>({
      // The login_link authenticator reads user/expires/hash from the
      // query string, not the body
      query: (params) => ({
        url: "security/login-link",
        method: "POST",
        params,
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(updateTokens(data))
        } catch {
          // noop
        }
      },
    }),
    applyVerifyEmail: build.mutation<void, Record<string, string>>({
      query: (params) => ({
        url: "verify-email",
        method: "POST",
        params,
      }),
      extraOptions: { withoutAuth: true },
    }),
    changePassword: build.mutation<void, ChangePasswordPayload>({
      query: (body) => ({
        url: "change-password",
        method: "POST",
        body,
      }),
    }),
    logout: build.mutation<void, string>({
      query: (payload) => ({
        url: "security/invalidate",
        method: "POST",
        body: { refreshToken: payload },
      }),
    }),
  }),
})

export const {
  useLoginMutation,
  useConnectOauthMutation,
  useRegisterMutation,
  useApplyLoginLinkMutation,
  useApplyVerifyEmailMutation,
  useChangePasswordMutation,
  useLogoutMutation,
} = api
